import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Grid,
  Collapse,
  SvgIcon,
  CircularProgress,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  EditOutlined,
  DeleteOutline,
} from '@mui/icons-material';

import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';

import {
  AppPrimaryButton,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import { GuardrailsService } from 'services/GuardrailsService';

import CreateGuardrailPackageModal from '../addPackage/CreateGuardrailPackageModal';
import { Toast } from 'shared/helpers';
import PolicyList from './PolicyList';
import useSize from 'shared/components/Hooks';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

interface GuardrailPolicy {
  guardrailPolicyId: string;
  enabled: boolean;
  _id: string;
}

interface GuardrailPackage {
  _id: string;
  packageName: string;
  description: string;
  policies: GuardrailPolicy[];
  createdBy: string;
  updatedBy: string | null;
  organizationId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  policyDetails: any[];
  totalEnabledPolicies: number;
}

const PromptPolicies: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAiExpanded, setIsAiExpanded] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openDeletePackage, setOpenDeletePackage] = useState(false);

  const [guardRailPackages, setGuardRailPackages] = useState<
    GuardrailPackage[]
  >([]);
  const [selectedPackage, setSelectedPackage] =
    useState<GuardrailPackage | null>(null);
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );
  const { isMobile } = useSize();

  // API Calls
  const loadGuardRail = (): void => {
    setIsLoading(true);
    GuardrailsService.getAllGuardRail()
      .then((response) => {
        const { packages } = response.data.data;

        setGuardRailPackages(packages || []);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // UI Actions
  const handleCloseDeletePackage = (): void => {
    setOpenDeletePackage(false);
  };

  const handleDeletePackage = (id: any): void => {
    setIsLoading(true);
    GuardrailsService.deletePackage(id)
      .then((response: any) => {
        if (response.data) {
          Toast.showSuccessMessage('Successfully deleted package');
          setOpenDeletePackage(false);
          loadGuardRail();
        }
      })
      .catch((e) => {
        Toast.showErrorMessage(e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditClick = (item: GuardrailPackage): void => {
    setSelectedPackage(item);
    setOpenCreateModal(true);
  };

  const handleDeleteClick = (item: GuardrailPackage): void => {
    setSelectedPackage(item);
    setOpenDeletePackage(true);
  };

  const handleCloseModal = (): void => {
    setOpenCreateModal(false);
    setSelectedPackage(null);
  };

  const handleSubmitPackage = (payload: any): void => {
    const isEdit = Boolean(selectedPackage?._id);

    const apiCall = isEdit
      ? GuardrailsService.updatePackage(selectedPackage!._id, payload)
      : GuardrailsService.createPackage(payload);

    apiCall
      .then(() => {
        Toast.showSuccessMessage(
          isEdit
            ? 'Successfully updated package'
            : 'Successfully created package'
        );

        handleCloseModal();
        loadGuardRail();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    loadGuardRail();
  }, []);

  if (isLoading) {
    <Box className="empty-content">
      <CircularProgress />
    </Box>;
  }

  return (
    <Box className="prompt-policies">
      <Box
        className="section-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box className="section-left">
          <IconButton size="small" className="arrow-btn">
            {isExpanded ? (
              <KeyboardArrowDown fontSize="small" />
            ) : (
              <KeyboardArrowRight fontSize="small" />
            )}
          </IconButton>

          <Typography className="section-title">Guardrail Packages</Typography>

          <Box className="count-chip">{guardRailPackages.length}</Box>
        </Box>
        {orgACLConfig.policies?.includes(
          'policies.guardrails.package.create'
        ) && (
          <AppPrimaryButton
            title={`${isMobile ? 'Create' : 'Create Package'}`}
            startIcon={
              isMobile ? (
                <></>
              ) : (
                <SvgIcon
                  component={Plus}
                  inheritViewBox
                  className="view-icon"
                  fontSize="inherit"
                  htmlColor="#76A81F"
                />
              )
            }
            className={isMobile ? 'btn-extra-small' : 'btn-small'}
            onClick={(e: any) => {
              e.stopPropagation();
              setOpenCreateModal(true);
            }}
          />
        )}
      </Box>
      <Collapse in={isExpanded}>
        <Grid container spacing={2}>
          {!isLoading &&
            guardRailPackages?.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item._id}>
                <Box className="package-card">
                  <Box className="card-top">
                    <Typography className="package-title">
                      {item.packageName}
                    </Typography>

                    <Box className="card-actions">
                      {orgACLConfig.policies?.includes(
                        'policies.guardrails.package.edit'
                      ) && (
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(item)}
                        >
                          <EditOutlined className="edit-icon" />
                        </IconButton>
                      )}

                      {orgACLConfig.policies?.includes(
                        'policies.guardrails.package.delete'
                      ) && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(item)}
                        >
                          <DeleteOutline className="delete-icon" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  <Typography className="package-description">
                    {item.description || '-'}
                  </Typography>

                  <Box className="card-footer">
                    <Typography className="policy-text">
                      {item.totalEnabledPolicies} policies enabled
                    </Typography>

                    {false && (
                      <Chip
                        label="0 AI systems"
                        size="small"
                        className="ai-chip"
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
        </Grid>
      </Collapse>
      {/* DIVIDER */}
      <Box className="divider" />
      {false && (
        <>
          {/* AI SYSTEM SECTION */}
          <Box
            className="section-header ai-section"
            onClick={() => setIsAiExpanded(!isAiExpanded)}
          >
            <Box className="section-left">
              <IconButton size="small" className="arrow-btn">
                {isAiExpanded ? (
                  <KeyboardArrowDown fontSize="small" />
                ) : (
                  <KeyboardArrowRight fontSize="small" />
                )}
              </IconButton>

              <Typography className="section-title">
                AI System Assignments
              </Typography>

              <Box className="count-chip">0</Box>
            </Box>
          </Box>
          <Collapse in={isAiExpanded}>
            <Box p={2}>
              <Typography>No AI systems found in your inventory.</Typography>
            </Box>
          </Collapse>
          <Box className="divider" />
        </>
      )}
      <PolicyList />

      {/* MODAL */}
      <CreateGuardrailPackageModal
        open={openCreateModal}
        onClose={handleCloseModal}
        editData={selectedPackage}
        onSubmit={handleSubmitPackage}
      />
      <ConfirmDialog
        title="Delete Package"
        subTitle={`Are you sure you want to delete "${selectedPackage?.packageName}" ?`}
        showConfirm={openDeletePackage}
        cancelTitle="Close"
        successTitle="Delete"
        onSuccess={() => {
          handleCloseDeletePackage();
          handleDeletePackage(selectedPackage?._id);
        }}
        onCancel={handleCloseDeletePackage}
        onCloseDialog={handleCloseDeletePackage}
      />
    </Box>
  );
};

export default PromptPolicies;
